import React, { useMemo, useState } from "react";
import { Button, Descriptions, Divider, Modal, message } from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  FileZipOutlined,
  QrcodeOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { QRCodeCanvas } from "qrcode.react";
import { createPdfDownloadName } from "../pages/helpers";
import { downloadAuthenticatedFile } from "../utils/downloadFile";

const OfficePdfResultActions = ({
  downloadUrl,
  originalFileName,
  outputType = "pdf",
  requiresAuth = false,
  stacked = false,
  className = "",
}) => {
  const [showQrModal, setShowQrModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isZip = outputType === "zip";
  const resultFileName = useMemo(
    () => {
      if (isZip) {
        return originalFileName || "batch_converted_pdfs.zip";
      }

      return createPdfDownloadName(originalFileName);
    },
    [isZip, originalFileName]
  );
  const qrElementId = useMemo(
    () => `office-file-qr-${resultFileName.replace(/[^a-z0-9_-]/gi, "-")}`,
    [resultFileName]
  );

  if (!downloadUrl) {
    return null;
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadAuthenticatedFile(downloadUrl, {
        token: requiresAuth ? localStorage.getItem("token") || "" : "",
        fileName: resultFileName,
      });
    } catch {
      message.error(`Unable to download ${isZip ? "ZIP" : "PDF"}. Please try again.`);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: resultFileName,
          text: `Open this ${isZip ? "ZIP" : "PDF"} file`,
          url: downloadUrl,
        });
        return;
      }

      await navigator.clipboard.writeText(downloadUrl);
      message.success("Public file link copied.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        message.error("Unable to share the file link.");
      }
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById(qrElementId);
    if (!canvas) {
      return;
    }

    const pngUrl = canvas.toDataURL("image/png");
    const anchor = document.createElement("a");
    anchor.download = `${resultFileName.replace(/\.[^.]+$/i, "")}-qr.png`;
    anchor.href = pngUrl;
    anchor.click();
  };

  return (
    <>
      <div
        className={`office-result-actions ${stacked ? "stacked" : ""} ${className}`.trim()}
      >
        <Button
          type="primary"
          className="convert-btn"
          icon={isZip ? <FileZipOutlined /> : <DownloadOutlined />}
          onClick={handleDownload}
          loading={downloading}
        >
          Download {isZip ? "ZIP" : "PDF"}
        </Button>
        <Button
          className="office-result-icon-btn"
          icon={<EyeOutlined />}
          onClick={() => window.open(downloadUrl, "_blank")}
          title={`View ${isZip ? "ZIP" : "PDF"}`}
        />
        <Button
          className="office-result-icon-btn"
          icon={<QrcodeOutlined />}
          onClick={() => setShowQrModal(true)}
          title="Generate QR"
        />
        <Button
          className="office-result-icon-btn"
          icon={<ShareAltOutlined />}
          onClick={handleShare}
          title="Share link"
        />
      </div>

      <Modal
        open={showQrModal}
        onCancel={() => setShowQrModal(false)}
        footer={null}
        width={500}
        title={<span className="office-qr-modal-title">{resultFileName}</span>}
        centered
      >
        <div className="office-qr-modal-body">
          <div className="office-qr-preview">
            <QRCodeCanvas
              id={qrElementId}
              value={downloadUrl}
              size={220}
              fgColor="#1a1a2e"
              bgColor="#ffffff"
              level="H"
              includeMargin={true}
            />
          </div>
          <Divider />
          <Descriptions
            column={1}
            size="small"
            labelStyle={{ fontWeight: 600, color: "#888", fontSize: 12 }}
            contentStyle={{ fontSize: 13 }}
          >
            <Descriptions.Item label="Type">{isZip ? "ZIP" : "PDF"} Link</Descriptions.Item>
            <Descriptions.Item label="File">{resultFileName}</Descriptions.Item>
            <Descriptions.Item label="URL">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="office-qr-link"
              >
                {downloadUrl}
              </a>
            </Descriptions.Item>
          </Descriptions>
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="office-qr-download-btn"
            onClick={handleDownloadQR}
          >
            Download QR PNG
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default OfficePdfResultActions;
